// FILE: src/blueprint/utils/formatCoachEntry.js

/**
 * Format one coach content block into a single inline chat-style string.
 *
 * Output rules:
 * - title + body => "**Title**: body"
 * - title only   => "**Title**"
 * - body only    => "body"
 * - neither      => ""
 *
 * Why this exists:
 * - The Coach typing component expects one text string.
 * - The incoming content may still be split into separate title/body fields.
 * - We want chat-like phrasing, not "title above paragraph" layout.
 *
 * Important:
 * - No floating ":" when body is missing.
 * - Empty or whitespace-only values are treated as missing.
 * - This is a pure formatter: no DOM, no React, no side effects.
 *
 * @param {Object} block
 * @returns {string}
 */
export function formatCoachEntry(block) {
  // Guard against null / undefined / non-object input.
  if (!block || typeof block !== 'object') {
    return '';
  }

  // Normalize title/body to trimmed strings.
  // Trimming here is intentional because this formatter builds a compact inline sentence.
  const title = typeof block.title === 'string' ? block.title.trim() : '';
  const body = typeof block.body === 'string' ? block.body.trim() : '';

  // Full chat-style line:
  // "**How to answer**: Answer what feels true, not what sounds impressive."
  if (title && body) {
    return `**${title}** ${body}`;
  }

  // Title-only entry:
  // "**Important**"
  if (title) {
    return `**${title}**`;
  }

  // Body-only entry:
  // "Answer what feels true, not what sounds impressive."
  if (body) {
    return body;
  }

  // Nothing usable.
  return '';
}
